/**
 * Tenant contract documents (Mietvertrag) section under direct-debit mandate.
 * @param {{ propertyHrId: string, unitId: string, unitContractId: number | null }} contractCtx
 */
const CONTRACT_DOCUMENTS_SECTION_ID = 'lux-contract-documents-section'
const CONTRACT_DOCUMENT_UPLOAD_LABEL = 'Vertragsdokument hinzufügen'
const CONTRACT_DOCUMENTS_LIST_CLASS = 'lux-contract-documents-list'
const contractDocumentMounts = new WeakMap()

// Impower tag names — same pattern as DEFAULT_IMPOWER_DOCUMENT_TAG_NAMES in document-impower-upload.ts
const CONTRACT_TENANT_DOCUMENT_UPLOAD_TAG_NAMES = [IMPOWER_FILED_DOCUMENT_TAG_NAME, 'Mietvertrag']
const CONTRACT_TENANT_DOCUMENT_SOURCE_TYPE = 'OTHER'
const CONTRACT_TENANT_DOCUMENT_OCR_STATE = 'NO_OCR'

const isTenantContractCreatePage = () => {
  const path = window.location.pathname
  if (/\/rent-plan\//.test(path)) return false
  return /\/contract\/all\/create\/?$/.test(path)
}

const resolveContractDocumentUploadContext = async (contractCtx, token) => {
  const contractId = contractCtx.unitContractId
  if (contractId == null) return null

  const [propertyData, unitContractData] = await Promise.all([
    fetch(`/services/pmp-accounting/api/v1/properties/${contractCtx.propertyHrId}`, {
      headers: { Authorization: 'Bearer ' + token },
    }).then((r) => {
      if (!r.ok) throw new Error('Objektdaten konnten nicht geladen werden.')
      return r.json()
    }),
    fetch(
      `/services/pmp-accounting/api/v1/unit-contract?unitContractIds=${encodeURIComponent(String(contractId))}`,
      { headers: { Authorization: 'Bearer ' + token } }
    ).then((r) => {
      if (!r.ok) throw new Error('Vertragsdaten konnten nicht geladen werden.')
      return r.json()
    }),
  ])

  const unitContract = Array.isArray(unitContractData) && unitContractData[0] ? unitContractData[0] : null
  if (!unitContract) throw new Error('Kein passender Einheitenvertrag gefunden.')

  const propertyId = Number(propertyData?.id)
  const unitId = Number(unitContract.unitId ?? contractCtx.unitId)
  const contactId = Number(
    unitContract.mailingContact?.contactId ??
    (Array.isArray(unitContract.contacts) && unitContract.contacts[0]
      ? unitContract.contacts[0].contactId
      : null)
  )

  if (!Number.isFinite(propertyId) || propertyId <= 0) throw new Error('Objekt-ID konnte nicht ermittelt werden.')
  if (!Number.isFinite(unitId) || unitId <= 0) throw new Error('Einheits-ID konnte nicht ermittelt werden.')

  return {
    contractId,
    propertyId,
    unitId,
    contactId: Number.isFinite(contactId) && contactId > 0 ? contactId : null,
  }
}

const getContractDocumentsListHost = (contentEl) => {
  let listHost = contentEl.querySelector(`.${CONTRACT_DOCUMENTS_LIST_CLASS}`)
  if (!listHost) {
    listHost = document.createElement('div')
    listHost.className = `FileUploadLine ${CONTRACT_DOCUMENTS_LIST_CLASS}`
    contentEl.appendChild(listHost)
  }
  return listHost
}

const loadContractDocumentList = async (contentEl, contractCtx) => {
  const token = Lux_ApiClient_getAuthToken()
  const contractId = contractCtx.unitContractId
  if (!token || contractId == null) return

  const listHost = getContractDocumentsListHost(contentEl)

  try {
    const docs = await fetchContractDocumentsForContract(contractId, token)
    listHost.querySelector('.impower-x-documents-container')?.remove()
    if (docs.length === 0) {
      listHost.remove()
      return
    }

    ListPropertyDocuments(docs, listHost, {
      allowDelete: !isTenantContractCreatePage(),
      confirmBeforeDelete: true,
      onDocumentDeleted: () => {
        void loadContractDocumentList(contentEl, contractCtx)
      },
    })
  } catch (err) {
    console.error('[Lux] Failed to load contract documents:', err)
    showToast('Dokumente konnten nicht geladen werden.', 'error')
  }
}

const applyContractDocumentUploadButtonState = (uploadBtn, canUpload) => {
  if (!uploadBtn) return
  if (canUpload) {
    uploadBtn.disabled = false
    uploadBtn.removeAttribute('disabled')
    uploadBtn.removeAttribute('aria-disabled')
    uploadBtn.classList.remove('ant-btn-disabled')
    uploadBtn.style.pointerEvents = ''
    uploadBtn.style.opacity = ''
    uploadBtn.style.cursor = 'pointer'
  } else {
    uploadBtn.disabled = true
    uploadBtn.setAttribute('disabled', '')
    uploadBtn.classList.add('ant-btn-disabled')
  }
}

const findLastschriftAddButton = (root) => {
  if (!root) return null
  for (const btn of root.querySelectorAll('button')) {
    const text = (btn.textContent || '').trim()
    if (/Lastschriftmandat/i.test(text) && /hinzufügen/i.test(text)) return btn
  }
  return null
}

const findLastschriftTitleRow = (addBtn) =>
  addBtn?.closest('.sub-section-title') || addBtn?.parentElement || null

const findLastschriftBlockEnd = (sectionContent) => {
  const addBtn = findLastschriftAddButton(sectionContent)
  if (!addBtn) return null

  const subSection = addBtn.closest('.sub-section')
  if (subSection && sectionContent.contains(subSection)) return subSection

  let blockStart = addBtn
  while (blockStart.parentElement && blockStart.parentElement !== sectionContent) {
    blockStart = blockStart.parentElement
  }
  if (blockStart.parentElement !== sectionContent) {
    blockStart =
      addBtn.closest('.sub-section-header') ||
      findLastschriftTitleRow(addBtn) ||
      addBtn.parentElement
    while (blockStart.parentElement && blockStart.parentElement !== sectionContent) {
      blockStart = blockStart.parentElement
    }
  }

  const children = Array.from(sectionContent.children)
  const startIdx = children.indexOf(blockStart)
  if (startIdx === -1) return blockStart

  let endIdx = startIdx
  for (let i = startIdx + 1; i < children.length; i++) {
    const child = children[i]
    if (child.id === CONTRACT_DOCUMENTS_SECTION_ID) break
    const text = child.textContent || ''
    const cls = child.className || ''
    if (
      /Lastschrift|SEPA|Mandat/i.test(text) ||
      cls.includes('sub-section-content') ||
      child.querySelector('.sub-section-content')
    ) {
      endIdx = i
      continue
    }
    break
  }
  return children[endIdx]
}

const setTitleRowLabel = (titleRow, label) => {
  if (!titleRow) return
  const actionBtn = titleRow.querySelector('button')
  const walker = document.createTreeWalker(titleRow, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (actionBtn && actionBtn.contains(node)) return NodeFilter.FILTER_REJECT
      if (!node.textContent.trim()) return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_ACCEPT
    },
  })
  const firstText = walker.nextNode()
  if (firstText) {
    firstText.textContent = label
    return
  }
  const labelEl = titleRow.querySelector('span:not(button span)')
  if (labelEl) labelEl.textContent = label
}

const applySubSectionSpacing = (subSection, sectionContent, insertAfter) => {
  const refSub =
    (insertAfter?.classList?.contains('sub-section') ? insertAfter : insertAfter?.closest('.sub-section')) ||
    sectionContent.querySelector('.sub-section')
  if (!refSub) return
  const styles = window.getComputedStyle(refSub)
  subSection.style.marginBottom = styles.marginBottom
  // margin-top: see #lux-contract-documents-section in injectContractDocumentsSectionStyles
}

const ensureTitleRowButtonRightAligned = (titleRow, uploadBtn, refTitleRow) => {
  if (!titleRow || !uploadBtn) return

  const ref = refTitleRow || titleRow
  const rowStyle = window.getComputedStyle(ref)
  titleRow.style.display =
    rowStyle.display === 'flex' || rowStyle.display === 'inline-flex' ? rowStyle.display : 'flex'
  titleRow.style.alignItems = rowStyle.alignItems || 'center'
  titleRow.style.justifyContent = rowStyle.justifyContent || 'flex-start'
  titleRow.style.width = '100%'
  titleRow.style.boxSizing = 'border-box'

  const header = titleRow.closest('.sub-section-header')
  if (header) {
    header.style.width = '100%'
    header.style.boxSizing = 'border-box'
  }

  uploadBtn.style.marginLeft = 'auto'
  uploadBtn.style.flexShrink = '0'
}

const createContractDocumentAddButton = (label) => {
  const btn = document.createElement('button')
  btn.type = 'button'
  btn.className = 'ant-btn ant-btn-default Button AdditionalEntityButton'
  btn.innerHTML =
    '<span role="img" class="anticon"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 7H9V0H7V7H0V9H7V16H9V9H16V7Z" fill="currentColor"></path></svg></span>' +
    `<span>${label}</span>`
  return btn
}

const buildContractDocumentsSection = (contractCtx, sectionContent) => {
  const addBtn = findLastschriftAddButton(sectionContent)
  const titleRowTemplate = findLastschriftTitleRow(addBtn)
  const headerTemplate = titleRowTemplate?.closest('.sub-section-header')
  const subSectionTemplate = addBtn?.closest('.sub-section')
  const contentTemplate = subSectionTemplate?.querySelector('.sub-section-content')

  const subSection = document.createElement('div')
  subSection.className = subSectionTemplate?.className || 'sub-section'
  subSection.id = CONTRACT_DOCUMENTS_SECTION_ID

  let uploadBtn
  if (titleRowTemplate) {
    const titleRow = titleRowTemplate.cloneNode(true)
    titleRow.querySelector('button')?.remove()
    setTitleRowLabel(titleRow, 'Vertragsdokumente')

    uploadBtn = addBtn
      ? addBtn.cloneNode(true)
      : createContractDocumentAddButton(CONTRACT_DOCUMENT_UPLOAD_LABEL)
    uploadBtn.id = 'contract-document-upload-button'
    const labelSpan = uploadBtn.querySelector('span:last-child')
    if (labelSpan) labelSpan.textContent = CONTRACT_DOCUMENT_UPLOAD_LABEL
    else uploadBtn.textContent = CONTRACT_DOCUMENT_UPLOAD_LABEL
    titleRow.appendChild(uploadBtn)
    ensureTitleRowButtonRightAligned(titleRow, uploadBtn, titleRowTemplate)

    if (headerTemplate) {
      const header = headerTemplate.cloneNode(false)
      header.appendChild(titleRow)
      subSection.appendChild(header)
    } else {
      subSection.appendChild(titleRow)
    }
  } else {
    const header = document.createElement('div')
    header.className = 'sub-section-header'
    const titleRow = document.createElement('div')
    titleRow.className = 'sub-section-title'
    titleRow.style.cssText = 'display:flex;align-items:center;width:100%;'
    const titleSpan = document.createElement('span')
    titleSpan.textContent = 'Vertragsdokumente'
    titleRow.appendChild(titleSpan)
    uploadBtn = createContractDocumentAddButton(CONTRACT_DOCUMENT_UPLOAD_LABEL)
    uploadBtn.id = 'contract-document-upload-button'
    titleRow.appendChild(uploadBtn)
    ensureTitleRowButtonRightAligned(titleRow, uploadBtn)
    header.appendChild(titleRow)
    subSection.appendChild(header)
  }

  const content = document.createElement('div')
  content.className = contentTemplate?.className || 'sub-section-content'
  subSection.appendChild(content)

  const canUpload =
    !isTenantContractCreatePage() && contractCtx.unitContractId != null
  applyContractDocumentUploadButtonState(uploadBtn, canUpload)

  const fileInput = document.createElement('input')
  fileInput.type = 'file'
  fileInput.multiple = true
  fileInput.accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png'
  fileInput.style.display = 'none'
  subSection.appendChild(fileInput)

  if (canUpload) {
    uploadBtn.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      fileInput.click()
    })

    fileInput.addEventListener('change', async () => {
      const files = [...fileInput.files]
      fileInput.value = ''
      if (!files.length) return

      const token = Lux_ApiClient_getAuthToken()
      if (!token) {
        showToast('Sie sind nicht angemeldet. Bitte melden Sie sich an und versuchen Sie es erneut.', 'error')
        return
      }

      addLoadingOverlay(content)
      try {
        const uploadContext = await resolveContractDocumentUploadContext(contractCtx, token)
        if (!uploadContext) {
          showToast('Das Hochladen des Dokuments ist fehlgeschlagen.', 'error')
          return
        }

        const uploadResult = await uploadImpowerDocumentsWithTag({
          files,
          uploadParamsBuilder: (file) => ({
            name: file.name.replace(/[\\/&?:]/g, '_'),
            propertyId: uploadContext.propertyId,
            unitId: uploadContext.unitId,
            contractId: uploadContext.contractId,
            assignedTo: 'UNIT',
            sourceType: CONTRACT_TENANT_DOCUMENT_SOURCE_TYPE,
            ocrState: CONTRACT_TENANT_DOCUMENT_OCR_STATE,
            ...(uploadContext.contactId ? { contactId: uploadContext.contactId } : {}),
          }),
          tagNames: CONTRACT_TENANT_DOCUMENT_UPLOAD_TAG_NAMES,
          token,
          apiBaseUrl: typeof baseApiUrl !== 'undefined' ? baseApiUrl : '',
          createMissingTags: true,
          patchFieldsBuilder: () => ({
            propertyId: uploadContext.propertyId,
            unitId: uploadContext.unitId,
            contractId: uploadContext.contractId,
            assignedTo: 'UNIT',
            contactId: uploadContext.contactId ?? undefined,
          }),
        })

        const { success, documents, failedFileNames = [], error } = uploadResult
        const totalCount = files.length
        const listOptions = {
          allowDelete: !isTenantContractCreatePage(),
          confirmBeforeDelete: true,
          onDocumentDeleted: () => {
            void loadContractDocumentList(content, contractCtx)
          },
        }

        if (documents.length) {
          if (error) {
            ListPropertyDocuments(documents, getContractDocumentsListHost(content), listOptions)
            showToast('Dokumente hochgeladen, Zuordnung fehlgeschlagen.', 'warning')
          } else {
            await loadContractDocumentList(content, contractCtx)
            if (success) {
              const count = documents.length
              showToast(
                count === 1 ? 'Dokument erfolgreich hochgeladen.' : `${count} Dokumente erfolgreich hochgeladen.`,
                'success'
              )
            }
          }
        }

        if (failedFileNames.length > 0) {
          const names = failedFileNames.join(', ')
          const failedCount = failedFileNames.length
          showToast(
            failedCount === 1
              ? `1 von ${totalCount} Dateien konnte nicht hochgeladen werden: ${names}`
              : `${failedCount} von ${totalCount} Dateien konnten nicht hochgeladen werden: ${names}`,
            'error'
          )
        } else if (!documents.length) {
          showToast(error || 'Dokument-Upload fehlgeschlagen.', 'error')
        }
      } catch (err) {
        console.error('[Lux] Contract document upload failed:', err)
        showToast(err?.message || 'Das Hochladen des Dokuments ist fehlgeschlagen.', 'error')
      } finally {
        removeLoadingOverlay(content)
      }
    })
  }

  return { subSection, content }
}

const injectContractDocuments = (contractCtx, editor) => {
  injectImpowerXStyles()

  const tenantEditor = editor || document.querySelector('.TenantContractEditor')
  if (!tenantEditor || !contractCtx?.propertyHrId) return
  const contractDetails = tenantEditor.querySelector('#ContractDetails')
  if (!contractDetails) return

  // PrimarySectionWrapper unmounts closed content. Mount documents when the user
  // opens Vertragsdetails; injecting documents must never select that step.
  let mount = contractDocumentMounts.get(contractDetails)
  if (!mount) {
    mount = { contractCtx }
    const observer = new MutationObserver(() => {
      if (contractDetails.isConnected) injectContractDocuments(mount.contractCtx, tenantEditor)
    })
    observer.observe(contractDetails, { childList: true, subtree: true })
    contractDocumentMounts.set(contractDetails, mount)
  }
  mount.contractCtx = contractCtx

  const existing = contractDetails.querySelector(`#${CONTRACT_DOCUMENTS_SECTION_ID}`)
  if (existing) {
    if (existing.querySelector('#contract-document-upload-button')) return
    existing.remove()
  }

  const wrapperContent = contractDetails.querySelector('.section-wrapper-content')
  if (!wrapperContent?.classList.contains('section-wrapper-open')) return

  const sectionContent = contractDetails.querySelector('.section-content')
  if (!sectionContent) return

  const { subSection, content } = buildContractDocumentsSection(contractCtx, sectionContent)

  const insertAfter = findLastschriftBlockEnd(sectionContent)
  applySubSectionSpacing(subSection, sectionContent, insertAfter)
  if (insertAfter && sectionContent.contains(insertAfter)) {
    insertAfter.insertAdjacentElement('afterend', subSection)
  } else {
    sectionContent.appendChild(subSection)
  }

  void loadContractDocumentList(content, contractCtx)
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { injectContractDocuments }
}
